/**
 * The measurement stack's shared shapes (docs/57 R6-18, r23 to r26; docs/research/round6-whole-business-2026-10.md
 * §1.9; docs/20 §12). One place for what the site script, the `sites` Worker, the lead consumer, the publish flow and
 * the acceptance matrix must agree on:
 *
 *   - the region and privacy signal `sites` stamps on every page, which decide the consent state in the browser
 *   - the GA4 names our own events are sent under, and which of them are key events
 *   - the lead's source record (landing page, referrer, UTM, gclid), captured first party in the browser and written
 *     into the lead row at `/_f/lead`, never shared with anyone
 *   - the per site IndexNow key, derived from the signing key both Workers hold, so nothing is stored
 */
import { z } from 'zod';
import { IsoDateTime } from './common.ts';

// ------------------------------------------------------------------------------------------------ region and consent

/**
 * Where the visitor is, for consent (r24: one strict US setup, California included). `us` is the United States and its
 * territories: no banner, Google Analytics on unless the visitor opts out or sends Global Privacy Control. Everything
 * else, an unknown country included, is `optin`: a banner with equal Accept and Reject, and nothing from Google loads
 * before Accept (basic mode, measurement spec §2.4).
 */
export const CONSENT_REGIONS = ['us', 'optin'] as const;
export type ConsentRegion = (typeof CONSENT_REGIONS)[number];

/** ISO 3166-1 codes that get the US setup: the states plus Puerto Rico, Guam, the US Virgin Islands, American Samoa, the Northern Marianas and the minor outlying islands. */
const US_COUNTRIES = new Set(['US', 'PR', 'GU', 'VI', 'AS', 'MP', 'UM']);

export function consentRegionOf(country: string | null | undefined): ConsentRegion {
  return country && US_COUNTRIES.has(country.toUpperCase()) ? 'us' : 'optin';
}

/** The attributes `sites` adds to `<html>` after the cache, so one cached page serves every visitor. */
export const REGION_ATTR = 'data-ff-region';
export const GPC_ATTR = 'data-ff-gpc';

/** CookieConsent's own cookie (strictly necessary: it holds only the choice). The site script reads it before Google loads. */
export const CONSENT_COOKIE = 'ff_cc';

/** The pinned consent manager (orestbida/cookieconsent, MIT). Bumping it means a new file name, so caches never mix two. */
export const COOKIECONSENT_VERSION = '3.1.0';
export const COOKIECONSENT_FILES = { js: `_a/cookieconsent-${COOKIECONSENT_VERSION}.js`, css: `_a/cookieconsent-${COOKIECONSENT_VERSION}.css` } as const;

/**
 * Google Consent Mode v2: every signal starts denied, before gtag.js is even requested (advanced mode's order). The
 * site script then updates `analytics_storage` alone when the state is granted. The three ad signals stay denied on
 * every site until a client buys the Ads line (R6-24), and there's no `ads` category to grant them before then.
 */
export const CONSENT_DEFAULT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'denied',
} as const;

/** How long gtag waits for the update after the default (ms). Our update is synchronous, so this only covers a slow page. */
export const CONSENT_WAIT_MS = 500;

// ------------------------------------------------------------------------------------------------ events

/**
 * The GA4 name each of our events is sent under. Names not listed go as themselves. `form_submit` becomes GA4's
 * recommended `generate_lead`. `page_view` is GA4's own (the config call sends it), so the site script never sends it
 * twice. `js_error` and `web_vitals` are for us only and never reach Google.
 */
export const GA4_EVENT_NAME: Readonly<Record<string, string>> = { form_submit: 'generate_lead', estimator_complete: 'generate_lead' };
export const NOT_SENT_TO_GA4 = ['page_view', 'js_error', 'web_vitals'] as const;

/** Key events, created through the Admin API before they ever fire (measurement spec §5.2). Two only: a form sent and a call tap. */
export const GA4_KEY_EVENTS = ['generate_lead', 'call_click'] as const;

/** Event scoped custom dimensions, from the beacon's allowlisted parameters. Never a form's contents. */
export const GA4_CUSTOM_DIMENSIONS = [
  { parameterName: 'form_id', displayName: 'Form' },
  { parameterName: 'cta_key', displayName: 'CTA' },
  { parameterName: 'service', displayName: 'Service' },
] as const;

export const Ga4MeasurementId = z.string().regex(/^G-[A-Z0-9]{6,12}$/);

// ------------------------------------------------------------------------------------------------ the lead's source

/** The query parameters the site keeps from a landing URL. Everything else is stripped before it's stored or sent anywhere. */
export const SOURCE_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'] as const;

const SourceText = z
  .string()
  .max(100)
  .regex(/^[^\u0000-\u001f\u007f<>"]*$/);

/**
 * Where an enquiry came from: the first page of the visit, the site that sent the visitor (origin and path only, no
 * query), the campaign tags and Google's click id when Ads exist. Captured in the visitor's own tab (sessionStorage,
 * not a cookie), sent only with a form they choose to send, stored only on their lead. First party, no cross site use.
 */
export const LeadOrigin = z.strictObject({
  landingPath: z.string().max(512).regex(/^\/(?![/\\])[^\s?#]*$/),
  referrer: z.url({ protocol: /^https?$/ }).max(512).nullable(),
  utmSource: SourceText.nullable(),
  utmMedium: SourceText.nullable(),
  utmCampaign: SourceText.nullable(),
  utmTerm: SourceText.nullable(),
  utmContent: SourceText.nullable(),
  gclid: z.string().max(200).regex(/^[A-Za-z0-9_-]+$/).nullable(),
  firstSeenAt: IsoDateTime.nullable(),
});
export type LeadOrigin = z.infer<typeof LeadOrigin>;

/** The hidden form field the site script fills from the tab's source record. */
export const SOURCE_FIELD = 'ff_src';

/** A lead with the same phone or email inside this many days is a repeat of the first, not a new enquiry (R6-18 ledger). */
export const DEDUPE_DAYS = 30;

// ------------------------------------------------------------------------------------------------ IndexNow

/**
 * The site's IndexNow key: 32 hex characters from an HMAC over the tenant id with the signing key `sites` and `jobs`
 * both hold. `sites` answers `/<key>.txt` with the key; `jobs` pings with it after every publish of a live site.
 * Nothing to store and nothing to rotate by hand (a new signing key gives every site a new key, which IndexNow allows).
 */
export async function indexNowKey(secret: string, tenantId: string): Promise<string> {
  if (!secret) throw new Error('indexNowKey needs the signing key');
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(`ff/indexnow/v1|${tenantId}`)));
  return [...sig.slice(0, 16)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const INDEXNOW_KEY_PATH = /^\/([a-f0-9]{32})\.txt$/;
/** The shared endpoint every IndexNow engine (Bing, Yandex, Seznam, Naver) reads from (indexnow.org/documentation). */
export const INDEXNOW_ENDPOINT = 'https://api.indexnow.org/indexnow';
/** The protocol's cap per request. */
export const INDEXNOW_MAX_URLS = 10_000;
